import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { LogOut, Eye, Send, Filter, RefreshCw, Star, Check, X, MessageSquare, Mail, Trash2, PanelLeftClose, PanelLeft, ShoppingCart, Clock, RotateCw, Globe, Reply, Users, Bot } from "lucide-react";
import { COUNTRIES } from "@/components/CountrySelectionModal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { Database } from "@/integrations/supabase/types";
import AdminSidebar from "@/components/admin/AdminSidebar";
import AdminErrorBoundary from "@/components/admin/AdminErrorBoundary";
import AdminAffiliatesSection from "@/components/admin/AdminAffiliatesSection";
import AdminEmailAnalyticsSection from "@/components/admin/AdminEmailAnalyticsSection";
import AdminAutoEmailsSection from "@/components/admin/AdminAutoEmailsSection";
import AdminChatbotSection from "@/components/admin/AdminChatbotSection";
import { useQuery } from "@tanstack/react-query";

// Helper pour calculer le reset à minuit heure française
const getNextMidnightParis = () => {
  const now = new Date();
  // Créer une date pour minuit Paris
  const parisFormatter = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  
  // Prochaine minuit Paris = aujourd'hui + 1 jour à 00:00
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split('T')[0];
  
  // Convertir en timestamp Paris minuit
  const midnightParis = new Date(`${tomorrowStr}T00:00:00+01:00`);
  return midnightParis;
};

const getTimeUntilReset = () => {
  const now = new Date();
  const nextReset = getNextMidnightParis();
  const diff = nextReset.getTime() - now.getTime();
  
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  return { hours, minutes };
};

// Obtenir la date actuelle en timezone Paris (YYYY-MM-DD)
const getTodayParis = () => {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Paris' });
};

type Request = Database["public"]["Tables"]["requests"]["Row"];
type RequestType = Database["public"]["Enums"]["request_type"];
type RequestStatus = Database["public"]["Enums"]["request_status"];

interface SubmittedReview {
  id: string;
  full_name: string;
  rating: number;
  review_text: string;
  status: string;
  created_at: string;
  approved_at: string | null;
}

interface ContactMessage {
  id: string;
  name: string;
  email: string;
  message: string;
  status: string;
  created_at: string;
  read_at: string | null;
  replied_at: string | null;
}

interface EscalatedTicket {
  id: string;
  session_id: string;
  user_email: string | null;
  messages: unknown; // Json type from Supabase
  status: string;
  created_at: string;
  updated_at: string;
}

interface TicketMessage {
  role: string;
  content: string;
  timestamp?: string;
}

// Helper to parse messages from Json
const parseTicketMessages = (messages: unknown): TicketMessage[] => {
  if (Array.isArray(messages)) {
    return messages as TicketMessage[];
  }
  return [];
};

// Email regex pattern to find emails in conversation
const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/gi;

// Helper to find email in conversation messages (fallback when user_email is null)
const findEmailInMessages = (messages: TicketMessage[]): string | null => {
  // Look through user messages for email addresses
  for (const msg of messages) {
    if (msg.role === "user") {
      const matches = msg.content.match(EMAIL_REGEX);
      if (matches && matches.length > 0) {
        return matches[0].toLowerCase();
      }
    }
  }
  return null;
};

// Get the best available email for a ticket
const getTicketEmail = (ticket: EscalatedTicket): string | null => {
  if (ticket.user_email) return ticket.user_email;
  const msgs = parseTicketMessages(ticket.messages);
  return findEmailInMessages(msgs);
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [requests, setRequests] = useState<Request[]>([]);
  const [reviews, setReviews] = useState<SubmittedReview[]>([]);
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [escalatedTickets, setEscalatedTickets] = useState<EscalatedTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [sending, setSending] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");
  const [reviewFilterStatus, setReviewFilterStatus] = useState<string>("pending");
  const [messageFilterStatus, setMessageFilterStatus] = useState<string>("new");
  const [activeTab, setActiveTab] = useState("orders");
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('admin-sidebar-open');
    return saved !== null ? JSON.parse(saved) : true;
  });
  
  // Save sidebar state to localStorage
  const handleToggleSidebar = () => {
    const newState = !sidebarOpen;
    setSidebarOpen(newState);
    localStorage.setItem('admin-sidebar-open', JSON.stringify(newState));
  };
  const [sendingFollowup, setSendingFollowup] = useState<string | null>(null);
  
  // Reply dialog state
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);
  const [replySubject, setReplySubject] = useState("");
  const [replyContent, setReplyContent] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  
  // Ticket reply dialog state
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<EscalatedTicket | null>(null);
  const [ticketReplyContent, setTicketReplyContent] = useState("");

  // Count unresolved chatbot conversations
  const { data: unresolvedChatbotCount = 0 } = useQuery({
    queryKey: ["chatbot-unresolved-count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("chatbot_conversations")
        .select("*", { count: "exact", head: true })
        .neq("status", "resolved");
      if (error) throw error;
      return count || 0;
    },
    refetchInterval: 30000,
  });
  const [sendingTicketReply, setSendingTicketReply] = useState(false);
  
  // Test codes counter
  const [testCodesUsed, setTestCodesUsed] = useState(0);
  const [maxTestCodes, setMaxTestCodes] = useState(10);
  const [timeUntilReset, setTimeUntilReset] = useState({ hours: 0, minutes: 0 });

  // Computed: check if no test codes are available
  const noTestCodesAvailable = testCodesUsed >= maxTestCodes;

  // All requests for sidebar (to calculate conversions)
  const allRequestsForSidebar = requests;

  useEffect(() => {
    checkAuthAndFetch();
  }, []);

  // Update countdown timer every minute
  useEffect(() => {
    setTimeUntilReset(getTimeUntilReset());
    const interval = setInterval(() => {
      setTimeUntilReset(getTimeUntilReset());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const checkAuthAndFetch = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate("/admin/login");
        return;
      }

      // Verify admin role
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id)
        .eq("role", "admin")
        .maybeSingle();

      if (!roleData) {
        await supabase.auth.signOut();
        navigate("/admin/login");
        return;
      }

      setIsAuthChecking(false);
      fetchRequests();
      fetchReviews();
      fetchMessages();
      fetchTestCodes();
      fetchEscalatedTickets();
    } catch (error) {
      console.error("Auth check error:", error);
      navigate("/admin/login");
    }
  };

  const fetchEscalatedTickets = async () => {
    try {
      const { data, error } = await supabase
        .from("chatbot_conversations")
        .select("*")
        .eq("status", "escalated")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setEscalatedTickets((data as EscalatedTicket[]) || []);
    } catch (error) {
      console.error("Error fetching escalated tickets:", error);
    }
  };

  const handleResolveTicket = async (ticketId: string) => {
    try {
      const { error } = await supabase
        .from("chatbot_conversations")
        .update({ 
          status: "resolved",
          resolved_at: new Date().toISOString()
        })
        .eq("id", ticketId);

      if (error) throw error;
      toast.success("Ticket résolu !");
      fetchEscalatedTickets();
    } catch (error) {
      console.error("Error resolving ticket:", error);
      toast.error("Erreur lors de la résolution du ticket");
    }
  };

  const openTicketDialog = (ticket: EscalatedTicket) => {
    setSelectedTicket(ticket);
    setTicketReplyContent("");
    setTicketDialogOpen(true);
  };

  const handleSendTicketReply = async () => {
    if (!selectedTicket) {
      toast.error("Aucun ticket sélectionné");
      return;
    }
    
    // Use getTicketEmail to find email from user_email OR from conversation
    const ticketEmail = getTicketEmail(selectedTicket);
    if (!ticketEmail) {
      toast.error("Aucun email disponible pour ce ticket");
      return;
    }
    
    if (!ticketReplyContent.trim()) {
      toast.error("Veuillez écrire un message");
      return;
    }

    setSendingTicketReply(true);
    try {
      const msgs = parseTicketMessages(selectedTicket.messages);
      const lastUserMsg = [...msgs].reverse().find(m => m.role === "user");
      
      const { error: emailError } = await supabase.functions.invoke("send-ticket-reply-email", {
        body: {
          ticketId: selectedTicket.id,
          email: ticketEmail,
          message: ticketReplyContent,
          conversationSummary: lastUserMsg?.content || undefined,
        },
      });

      if (emailError) {
        console.error("Email error:", emailError);
        throw new Error("Erreur lors de l'envoi de l'email");
      }

      toast.success("Réponse envoyée par email !");
      setTicketDialogOpen(false);
      fetchEscalatedTickets();
    } catch (error: any) {
      console.error("Error sending ticket reply:", error);
      toast.error(error.message || "Erreur lors de l'envoi");
    } finally {
      setSendingTicketReply(false);
    }
  };

  const fetchTestCodes = async () => {
    try {
      const todayParis = getTodayParis();
      
      // Try to get today's record
      let { data, error } = await supabase
        .from("daily_test_codes")
        .select("*")
        .eq("date", todayParis)
        .maybeSingle();

      if (error) throw error;

      // If no record for today, create one
      if (!data) {
        const { data: newData, error: insertError } = await supabase
          .from("daily_test_codes")
          .insert({ date: todayParis, codes_used: 0, max_codes: 10 })
          .select()
          .single();
        
        if (insertError) throw insertError;
        data = newData;
      }

      if (data) {
        setTestCodesUsed(data.codes_used);
        setMaxTestCodes(data.max_codes);
      }
    } catch (error) {
      console.error("Error fetching test codes:", error);
    }
  };

  const incrementTestCodeUsage = async () => {
    try {
      const todayParis = getTodayParis();
      
      const { error } = await supabase
        .from("daily_test_codes")
        .update({ codes_used: testCodesUsed + 1 })
        .eq("date", todayParis);

      if (error) throw error;
      
      setTestCodesUsed(prev => prev + 1);
    } catch (error) {
      console.error("Error incrementing test codes:", error);
    }
  };

  const fetchMessages = async () => {
    setMessagesLoading(true);
    try {
      const { data, error } = await supabase
        .from("contact_messages")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setMessages((data as ContactMessage[]) || []);
    } catch (error: any) {
      console.error("Error fetching messages:", error);
      toast.error("Erreur lors du chargement des messages");
    } finally {
      setMessagesLoading(false);
    }
  };

  const fetchReviews = async () => {
    setReviewsLoading(true);
    try {
      const { data, error } = await supabase
        .from("submitted_reviews")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setReviews((data as SubmittedReview[]) || []);
    } catch (error: any) {
      console.error("Error fetching reviews:", error);
      toast.error("Erreur lors du chargement des avis");
    } finally {
      setReviewsLoading(false);
    }
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("requests")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setRequests(data || []);
      
      // Si les codes sont épuisés, marquer automatiquement les nouvelles demandes test comme codes_exhausted
      if (noTestCodesAvailable && data) {
        const unmarkedTestRequests = data.filter(
          r => r.type === "test_24h" && 
               r.status === "new" && 
               r.credentials_sent_at === null &&
               r.codes_exhausted !== true
        );
        
        if (unmarkedTestRequests.length > 0) {
          console.log("Marking test requests as codes_exhausted:", unmarkedTestRequests.map(r => r.email));
          
          for (const req of unmarkedTestRequests) {
            await supabase
              .from("requests")
              .update({ codes_exhausted: true })
              .eq("id", req.id);
          }
          
          // Refetch pour mettre à jour l'UI
          const { data: refreshedData } = await supabase
            .from("requests")
            .select("*")
            .order("created_at", { ascending: false });
          
          if (refreshedData) {
            setRequests(refreshedData);
          }
        }
      }
    } catch (error: any) {
      toast.error("Erreur lors du chargement des demandes");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/admin/login");
  };

  const openRequestDialog = (request: Request) => {
    setSelectedRequest(request);
    setUsername(request.username || "");
    setPassword(request.password || "");
    setDialogOpen(true);
  };

  const handleSendCredentials = async () => {
    if (!selectedRequest) return;
    
    if (!username.trim() || !password.trim()) {
      toast.error("Veuillez remplir le nom d'utilisateur et le mot de passe");
      return;
    }

    setSending(true);
    try {
      // Send email via Resend
      const { error: emailError } = await supabase.functions.invoke("send-credentials-email", {
        body: {
          email: selectedRequest.email,
          username,
          password,
          type: selectedRequest.type,
        },
      });

      if (emailError) {
        console.error("Email error:", emailError);
        throw new Error("Erreur lors de l'envoi de l'email");
      }

      // Update request with credentials and status
      const { error } = await supabase
        .from("requests")
        .update({
          username,
          password,
          status: "credentials_sent" as RequestStatus,
          credentials_sent_at: new Date().toISOString(),
        })
        .eq("id", selectedRequest.id);

      if (error) throw error;

      // Increment test code counter if it's a test_24h
      if (selectedRequest.type === "test_24h") {
        await incrementTestCodeUsage();
      }

      toast.success("Identifiants envoyés par email avec succès !");
      
      setDialogOpen(false);
      fetchRequests();
    } catch (error: any) {
      console.error("Error sending credentials:", error);
      toast.error(error.message || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const handleSendFollowup = async (request: Request, followupType: "reminder_24h" | "special_offer") => {
    setSendingFollowup(request.id);
    try {
      const { error } = await supabase.functions.invoke("send-followup-email", {
        body: {
          email: request.email,
          type: followupType,
        },
      });

      if (error) throw error;

      // Mettre à jour followup_sent_at
      await supabase
        .from("requests")
        .update({ followup_sent_at: new Date().toISOString() })
        .eq("id", request.id);

      toast.success("Email de relance envoyé !");
      fetchRequests();
    } catch (error: any) {
      console.error("Error sending followup:", error);
      toast.error("Erreur lors de l'envoi de la relance");
    } finally {
      setSendingFollowup(null);
    }
  };

  const getTypeLabel = (type: RequestType | string) => {
    switch (type) {
      case "test_24h":
        return "Test 24h";
      case "3months":
        return "3 mois";
      case "6months":
        return "6 mois";
      case "12months":
        return "12 mois";
      case "sub_monthly":
        return "Mensuel";
      case "sub_yearly":
        return "Annuel";
      default:
        return type;
    }
  };

  const getTypeBadgeColor = (type: RequestType | string) => {
    switch (type) {
      case "test_24h":
        return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      case "3months":
        return "bg-emerald-500/20 text-emerald-400 border-emerald-500/30";
      case "6months":
        return "bg-purple-500/20 text-purple-400 border-purple-500/30";
      case "12months":
        return "bg-amber-500/20 text-amber-400 border-amber-500/30";
      case "sub_monthly":
        return "bg-green-500/20 text-green-400 border-green-500/30";
      case "sub_yearly":
        return "bg-amber-500/20 text-amber-400 border-amber-500/30";
      default:
        return "";
    }
  };

  const filteredRequests = requests.filter((req) => {
    if (filterStatus !== "all" && req.status !== filterStatus) return false;
    if (filterType !== "all" && req.type !== filterType) return false;
    return true;
  });

  const filteredReviews = reviews.filter((review) => {
    if (reviewFilterStatus !== "all" && review.status !== reviewFilterStatus) return false;
    return true;
  });

  const filteredMessages = messages.filter((msg) => {
    if (messageFilterStatus !== "all" && msg.status !== messageFilterStatus) return false;
    return true;
  });

  const handleMarkMessageRead = async (messageId: string) => {
    try {
      const { error } = await supabase
        .from("contact_messages")
        .update({ 
          status: "read", 
          read_at: new Date().toISOString() 
        })
        .eq("id", messageId);

      if (error) throw error;
      toast.success("Message marqué comme lu");
      fetchMessages();
    } catch (error) {
      console.error("Error marking message read:", error);
      toast.error("Erreur");
    }
  };

  const handleMarkMessageReplied = async (messageId: string) => {
    try {
      const { error } = await supabase
        .from("contact_messages")
        .update({ 
          status: "replied", 
          replied_at: new Date().toISOString() 
        })
        .eq("id", messageId);

      if (error) throw error;
      toast.success("Message marqué comme répondu");
      fetchMessages();
    } catch (error) {
      console.error("Error marking message replied:", error);
      toast.error("Erreur");
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    try {
      const { error } = await supabase
        .from("contact_messages")
        .delete()
        .eq("id", messageId);

      if (error) throw error;
      toast.success("Message supprimé");
      fetchMessages();
    } catch (error) {
      console.error("Error deleting message:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const openReplyDialog = (msg: ContactMessage) => {
    setSelectedMessage(msg);
    setReplySubject(`Re: Votre message - Roi du Streaming`);
    setReplyContent("");
    setReplyDialogOpen(true);
  };

  const handleSendReply = async () => {
    if (!selectedMessage) return;
    
    if (!replyContent.trim()) {
      toast.error("Veuillez écrire un message");
      return;
    }

    setSendingReply(true);
    try {
      const { error: emailError } = await supabase.functions.invoke("send-reply-email", {
        body: {
          email: selectedMessage.email,
          name: selectedMessage.name,
          subject: replySubject,
          message: replyContent,
          originalMessage: selectedMessage.message,
        },
      });

      if (emailError) {
        console.error("Email error:", emailError);
        throw new Error("Erreur lors de l'envoi de l'email");
      }

      // Mark message as replied
      const { error } = await supabase
        .from("contact_messages")
        .update({ 
          status: "replied", 
          replied_at: new Date().toISOString() 
        })
        .eq("id", selectedMessage.id);

      if (error) throw error;

      toast.success("Réponse envoyée avec succès !");
      setReplyDialogOpen(false);
      fetchMessages();
    } catch (error: any) {
      console.error("Error sending reply:", error);
      toast.error(error.message || "Erreur lors de l'envoi");
    } finally {
      setSendingReply(false);
    }
  };

  const handleApproveReview = async (reviewId: string) => {
    try {
      const { error } = await supabase
        .from("submitted_reviews")
        .update({ 
          status: "approved", 
          approved_at: new Date().toISOString() 
        })
        .eq("id", reviewId);

      if (error) throw error;
      toast.success("Avis approuvé !");
      fetchReviews();
    } catch (error) {
      console.error("Error approving review:", error);
      toast.error("Erreur lors de l'approbation");
    }
  };

  const handleRejectReview = async (reviewId: string) => {
    try {
      const { error } = await supabase
        .from("submitted_reviews")
        .update({ status: "rejected" })
        .eq("id", reviewId);

      if (error) throw error;
      toast.success("Avis rejeté");
      fetchReviews();
    } catch (error) {
      console.error("Error rejecting review:", error);
      toast.error("Erreur lors du rejet");
    }
  };

  const handleDeleteReview = async (reviewId: string) => {
    try {
      const { error } = await supabase
        .from("submitted_reviews")
        .delete()
        .eq("id", reviewId);

      if (error) throw error;
      toast.success("Avis supprimé");
      fetchReviews();
    } catch (error) {
      console.error("Error deleting review:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleDeleteRequest = async (requestId: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cette commande ?")) return;
    
    try {
      const { error } = await supabase
        .from("requests")
        .delete()
        .eq("id", requestId);

      if (error) throw error;
      toast.success("Commande supprimée");
      fetchRequests();
    } catch (error) {
      console.error("Error deleting request:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const renderStars = (rating: number) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? 'fill-primary text-primary' : 'text-muted-foreground'}`}
      />
    ));
  };

  // Show loading screen while checking auth
  if (isAuthChecking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <AdminErrorBoundary fallbackMessage="Erreur dans le dashboard admin">
      <div className="min-h-screen bg-background flex">
        {/* Sidebar */}
        {sidebarOpen && (
          <AdminErrorBoundary fallbackMessage="Erreur dans la sidebar">
            <AdminSidebar 
              requests={allRequestsForSidebar} 
              onRefresh={fetchRequests} 
              noTestCodesAvailable={noTestCodesAvailable}
            />
          </AdminErrorBoundary>
        )}

      {/* Main Content */}
      <div className="flex-1 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleToggleSidebar}
                className="text-muted-foreground hover:text-foreground"
              >
                {sidebarOpen ? <PanelLeftClose className="w-5 h-5" /> : <PanelLeft className="w-5 h-5" />}
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Dashboard Admin</h1>
                <p className="text-muted-foreground">Gestion des demandes de test et abonnements</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="border-border text-foreground hover:bg-muted"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>

          {/* Escalated Tickets Alert */}
          {escalatedTickets.length > 0 && (
            <Card className="bg-red-950/50 border-2 border-red-500 mb-6 animate-pulse">
              <CardHeader className="pb-2">
                <CardTitle className="text-red-400 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  🚨 {escalatedTickets.length} Ticket{escalatedTickets.length > 1 ? 's' : ''} en attente
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {escalatedTickets.map((ticket) => {
                    const msgs = parseTicketMessages(ticket.messages);
                    const lastUserMsg = [...msgs].reverse().find(m => m.role === "user");
                    const detectedEmail = getTicketEmail(ticket);
                    return (
                      <div 
                        key={ticket.id} 
                        className="bg-red-900/30 border border-red-500/50 rounded-lg p-4 flex flex-col md:flex-row md:items-center justify-between gap-3"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-red-300">
                              {detectedEmail || "Email non fourni"}
                              {detectedEmail && !ticket.user_email && (
                                <span className="text-xs text-amber-400 ml-2">(détecté)</span>
                              )}
                            </span>
                            <span className="text-xs text-red-400/70">
                              {new Date(ticket.created_at).toLocaleString("fr-FR")}
                            </span>
                          </div>
                          {lastUserMsg && (
                            <p className="text-sm text-red-200/80 truncate max-w-xl">
                              "{lastUserMsg.content}"
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-500/50 text-red-300 hover:bg-red-900/50"
                            onClick={() => openTicketDialog(ticket)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Voir
                          </Button>
                          {getTicketEmail(ticket) && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-blue-500/50 text-blue-300 hover:bg-blue-900/50"
                              onClick={() => openTicketDialog(ticket)}
                            >
                              <Reply className="w-4 h-4 mr-1" />
                              Répondre
                            </Button>
                          )}
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => handleResolveTicket(ticket.id)}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Résolu
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          {/* Test Codes Counter - Prominent */}
          <Card className={`bg-card border-2 ${maxTestCodes - testCodesUsed <= 2 ? 'border-red-500' : 'border-primary'}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <p className="text-muted-foreground text-sm">Codes tests</p>
              </div>
              <p className={`text-3xl font-bold ${maxTestCodes - testCodesUsed <= 2 ? 'text-red-400' : 'text-primary'}`}>
                {Math.max(0, maxTestCodes - testCodesUsed)}/{maxTestCodes}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Reset dans {timeUntilReset.hours}h{timeUntilReset.minutes.toString().padStart(2, '0')}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <p className="text-muted-foreground text-sm">Total demandes</p>
              <p className="text-3xl font-bold text-foreground">{requests.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <p className="text-muted-foreground text-sm">Nouvelles</p>
              <p className="text-3xl font-bold text-primary">
                {requests.filter((r) => r.status === "new" && !r.codes_exhausted).length}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <p className="text-muted-foreground text-sm">Envoyées</p>
              <p className="text-3xl font-bold text-secondary">
                {requests.filter((r) => r.status === "credentials_sent").length}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <p className="text-muted-foreground text-sm">Avis en attente</p>
              <p className="text-3xl font-bold text-amber-400">
                {reviews.filter((r) => r.status === "pending").length}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <p className="text-muted-foreground text-sm">Messages</p>
              <p className="text-3xl font-bold text-green-400">
                {messages.filter((m) => m.status === "new").length}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-muted">
            <TabsTrigger value="orders" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <ShoppingCart className="w-4 h-4 mr-2" />
              Commandes ({requests.filter(r => {
                if (r.type === "test_24h" && noTestCodesAvailable) return false;
                return r.status === "new" && !r.codes_exhausted;
              }).length})
            </TabsTrigger>
            <TabsTrigger value="requests" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Historique ({requests.length})
            </TabsTrigger>
            <TabsTrigger value="reviews" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Star className="w-4 h-4 mr-2" />
              Avis ({reviews.filter(r => r.status === "pending").length})
            </TabsTrigger>
            <TabsTrigger value="messages" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Mail className="w-4 h-4 mr-2" />
              Messages ({messages.filter(m => m.status === "new").length})
            </TabsTrigger>
            <TabsTrigger value="affiliates" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Users className="w-4 h-4 mr-2" />
              Affiliés
            </TabsTrigger>
            <TabsTrigger value="auto-emails" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Clock className="w-4 h-4 mr-2" />
              Relances Auto
            </TabsTrigger>
            <TabsTrigger value="email-analytics" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Mail className="w-4 h-4 mr-2" />
              Analytics Emails
            </TabsTrigger>
            <TabsTrigger value="chatbot" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground relative">
              <Bot className="w-4 h-4 mr-2" />
              Chatbot SAV
              {unresolvedChatbotCount > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-xs font-bold animate-pulse">
                  {unresolvedChatbotCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Orders Tab - Only NEW requests */}
          <TabsContent value="orders" className="space-y-6">
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-foreground">
                  Commandes à traiter ({requests.filter(r => {
                    // Exclure les tests quand il n'y a plus de codes disponibles
                    if (r.type === "test_24h" && noTestCodesAvailable) return false;
                    return r.status === "new" && !r.codes_exhausted;
                  }).length})
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchRequests}
                  className="border-border"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Actualiser
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">Chargement...</div>
                ) : requests.filter(r => {
                    if (r.type === "test_24h" && noTestCodesAvailable) return false;
                    return r.status === "new" && !r.codes_exhausted;
                  }).length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    ✅ Aucune commande en attente
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border">
                          <TableHead className="text-muted-foreground">Email</TableHead>
                          <TableHead className="text-muted-foreground">Type</TableHead>
                          <TableHead className="text-muted-foreground">Pays</TableHead>
                          <TableHead className="text-muted-foreground">Date</TableHead>
                          <TableHead className="text-muted-foreground">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {requests
                          .filter(r => {
                            if (r.type === "test_24h" && noTestCodesAvailable) return false;
                            return r.status === "new" && !r.codes_exhausted;
                          })
                          .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                          .map((request) => (
                            <TableRow key={request.id} className="border-border">
                              <TableCell className="text-foreground font-medium">
                                {request.email}
                              </TableCell>
                              <TableCell>
                                <Badge className={getTypeBadgeColor(request.type)}>
                                  {getTypeLabel(request.type)}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-muted-foreground">
                                {request.selected_countries && request.selected_countries.length > 0 ? (
                                  <div className="flex items-center gap-1">
                                    <Globe className="w-3 h-3" />
                                    <span className="text-xs">
                                      {request.selected_countries.map(code => 
                                        COUNTRIES.find(c => c.code === code)?.name || code
                                      ).join(", ")}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="text-xs text-muted-foreground/50">-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-muted-foreground">
                                {new Date(request.created_at).toLocaleDateString("fr-FR", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() => openRequestDialog(request)}
                                    className="bg-primary hover:bg-primary/90"
                                  >
                                    <Send className="w-4 h-4 mr-1" />
                                    Traiter
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => handleDeleteRequest(request.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="requests" className="space-y-6">
            {/* Filters */}
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Filtres :</span>
                  </div>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-40 bg-input border-border">
                      <SelectValue placeholder="Statut" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les statuts</SelectItem>
                      <SelectItem value="new">Nouveaux</SelectItem>
                      <SelectItem value="credentials_sent">Envoyés</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="w-40 bg-input border-border">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les types</SelectItem>
                      <SelectItem value="test_24h">Test 24h</SelectItem>
                      <SelectItem value="3months">3 mois</SelectItem>
                      <SelectItem value="6months">6 mois</SelectItem>
                      <SelectItem value="12months">12 mois</SelectItem>
                      <SelectItem value="sub_monthly">Mensuel</SelectItem>
                      <SelectItem value="sub_yearly">Annuel</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchRequests}
                    className="border-border"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Actualiser
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Table */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-foreground">Demandes ({filteredRequests.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">Chargement...</div>
                ) : filteredRequests.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">Aucune demande</div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border">
                          <TableHead className="text-muted-foreground">Email</TableHead>
                          <TableHead className="text-muted-foreground">Type</TableHead>
                          <TableHead className="text-muted-foreground">Pays</TableHead>
                          <TableHead className="text-muted-foreground">Date</TableHead>
                          <TableHead className="text-muted-foreground">Statut</TableHead>
                          <TableHead className="text-muted-foreground">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredRequests.map((request) => (
                          <TableRow key={request.id} className="border-border">
                            <TableCell className="text-foreground font-medium">
                              {request.email}
                            </TableCell>
                            <TableCell>
                              <Badge className={getTypeBadgeColor(request.type)}>
                                {getTypeLabel(request.type)}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {request.selected_countries && request.selected_countries.length > 0 ? (
                                <div className="flex items-center gap-1">
                                  <Globe className="w-3 h-3" />
                                  <span className="text-xs">
                                    {request.selected_countries.map(code => 
                                      COUNTRIES.find(c => c.code === code)?.name || code
                                    ).join(", ")}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground/50">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {new Date(request.created_at).toLocaleDateString("fr-FR", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </TableCell>
                            <TableCell>
                              <Badge
                                className={
                                  request.status === "new"
                                    ? "bg-primary/20 text-primary border-primary/30"
                                    : "bg-secondary/20 text-secondary border-secondary/30"
                                }
                              >
                                {request.status === "new" ? "Nouveau" : "Envoyé"}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openRequestDialog(request)}
                                className="border-border"
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                Ouvrir
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews" className="space-y-6">
            {/* Filters */}
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Filtres :</span>
                  </div>
                  <Select value={reviewFilterStatus} onValueChange={setReviewFilterStatus}>
                    <SelectTrigger className="w-40 bg-input border-border">
                      <SelectValue placeholder="Statut" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les statuts</SelectItem>
                      <SelectItem value="pending">En attente</SelectItem>
                      <SelectItem value="approved">Approuvés</SelectItem>
                      <SelectItem value="rejected">Rejetés</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchReviews}
                    className="border-border"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Actualiser
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Reviews List */}
            <div className="grid gap-4">
              {reviewsLoading ? (
                <div className="text-center py-8 text-muted-foreground">Chargement...</div>
              ) : filteredReviews.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">Aucun avis</div>
              ) : (
                filteredReviews.map((review) => (
                  <Card key={review.id} className="bg-card border-border">
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-primary-foreground font-bold">
                              {review.full_name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-semibold text-foreground">{review.full_name}</p>
                              <div className="flex gap-0.5">
                                {renderStars(review.rating)}
                              </div>
                            </div>
                            <Badge
                              className={
                                review.status === "pending"
                                  ? "bg-amber-500/20 text-amber-400 border-amber-500/30"
                                  : review.status === "approved"
                                  ? "bg-green-500/20 text-green-400 border-green-500/30"
                                  : "bg-red-500/20 text-red-400 border-red-500/30"
                              }
                            >
                              {review.status === "pending" ? "En attente" : review.status === "approved" ? "Approuvé" : "Rejeté"}
                            </Badge>
                          </div>
                          <p className="text-muted-foreground mb-2">"{review.review_text}"</p>
                          <p className="text-xs text-muted-foreground">
                            Soumis le {new Date(review.created_at).toLocaleDateString("fr-FR", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          {review.status === "pending" && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleApproveReview(review.id)}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Approuver
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRejectReview(review.id)}
                                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                              >
                                <X className="w-4 h-4 mr-1" />
                                Rejeter
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteReview(review.id)}
                            className="text-muted-foreground hover:text-destructive"
                          >
                            Supprimer
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" className="space-y-6">
            {/* Filters */}
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Filtres :</span>
                  </div>
                  <Select value={messageFilterStatus} onValueChange={setMessageFilterStatus}>
                    <SelectTrigger className="w-40 bg-input border-border">
                      <SelectValue placeholder="Statut" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les statuts</SelectItem>
                      <SelectItem value="new">Nouveaux</SelectItem>
                      <SelectItem value="read">Lus</SelectItem>
                      <SelectItem value="replied">Répondus</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchMessages}
                    className="border-border"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Actualiser
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Messages List */}
            <div className="grid gap-4">
              {messagesLoading ? (
                <div className="text-center py-8 text-muted-foreground">Chargement...</div>
              ) : filteredMessages.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">Aucun message</div>
              ) : (
                filteredMessages.map((msg) => (
                  <Card key={msg.id} className={`bg-card border-border ${msg.status === "new" ? "border-l-4 border-l-green-500" : ""}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-bold">
                              {msg.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-semibold text-foreground">{msg.name}</p>
                              <a href={`mailto:${msg.email}`} className="text-sm text-primary hover:underline">
                                {msg.email}
                              </a>
                            </div>
                            <Badge
                              className={
                                msg.status === "new"
                                  ? "bg-green-500/20 text-green-400 border-green-500/30"
                                  : msg.status === "read"
                                  ? "bg-blue-500/20 text-blue-400 border-blue-500/30"
                                  : "bg-purple-500/20 text-purple-400 border-purple-500/30"
                              }
                            >
                              {msg.status === "new" ? "Nouveau" : msg.status === "read" ? "Lu" : "Répondu"}
                            </Badge>
                          </div>
                          <p className="text-foreground mb-2 whitespace-pre-wrap">{msg.message}</p>
                          <p className="text-xs text-muted-foreground">
                            Reçu le {new Date(msg.created_at).toLocaleDateString("fr-FR", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </p>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          {msg.status === "new" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleMarkMessageRead(msg.id)}
                              className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Marquer lu
                            </Button>
                          )}
                          <Button
                            size="sm"
                            onClick={() => openReplyDialog(msg)}
                            className="bg-primary hover:bg-primary/90"
                          >
                            <Reply className="w-4 h-4 mr-1" />
                            Répondre
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteMessage(msg.id)}
                            className="text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Affiliates Tab */}
          <TabsContent value="affiliates" className="space-y-6">
            <AdminAffiliatesSection />
          </TabsContent>

          {/* Auto Emails Tab */}
          <TabsContent value="auto-emails" className="space-y-6">
            <AdminAutoEmailsSection />
          </TabsContent>

          {/* Email Analytics Tab */}
          <TabsContent value="email-analytics" className="space-y-6">
            <AdminEmailAnalyticsSection />
          </TabsContent>

          {/* Chatbot SAV Tab */}
          <TabsContent value="chatbot" className="space-y-6">
            <AdminChatbotSection />
          </TabsContent>
        </Tabs>
      </div>

      {/* Request Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border text-foreground max-w-md">
          <DialogHeader>
            <DialogTitle>Détails de la demande</DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Email</Label>
                <p className="text-foreground font-medium">{selectedRequest.email}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Type</Label>
                  <p className="text-foreground">{getTypeLabel(selectedRequest.type)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Statut</Label>
                  <p className="text-foreground">
                    {selectedRequest.status === "new" ? "Nouveau" : "Envoyé"}
                  </p>
                </div>
              </div>
              <div>
                <Label className="text-muted-foreground">Date de création</Label>
                <p className="text-foreground">
                  {new Date(selectedRequest.created_at).toLocaleString("fr-FR")}
                </p>
              </div>
              {selectedRequest.credentials_sent_at && (
                <div>
                  <Label className="text-muted-foreground">Identifiants envoyés le</Label>
                  <p className="text-foreground">
                    {new Date(selectedRequest.credentials_sent_at).toLocaleString("fr-FR")}
                  </p>
                </div>
              )}
              {selectedRequest.followup_sent_at && (
                <div>
                  <Label className="text-muted-foreground">Relance envoyée le</Label>
                  <p className="text-foreground">
                    {new Date(selectedRequest.followup_sent_at).toLocaleString("fr-FR")}
                  </p>
                </div>
              )}

              <hr className="border-border" />

              <div className="space-y-2">
                <Label htmlFor="username">Nom d'utilisateur</Label>
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Entrez le nom d'utilisateur"
                  className="bg-input border-border"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <Input
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Entrez le mot de passe"
                  className="bg-input border-border"
                />
              </div>

              <Button
                onClick={handleSendCredentials}
                disabled={sending}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                <Send className="w-4 h-4 mr-2" />
                {sending ? "Envoi en cours..." : "Envoyer les identifiants"}
              </Button>

              {selectedRequest.status === "credentials_sent" && (
                <div className="pt-2 space-y-2">
                  <hr className="border-border" />
                  <Label className="text-muted-foreground">Emails de relance</Label>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSendFollowup(selectedRequest, "reminder_24h")}
                      disabled={sendingFollowup === selectedRequest.id}
                      className="flex-1 border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                    >
                      <RotateCw className={`w-4 h-4 mr-1 ${sendingFollowup === selectedRequest.id ? 'animate-spin' : ''}`} />
                      Rappel
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSendFollowup(selectedRequest, "special_offer")}
                      disabled={sendingFollowup === selectedRequest.id}
                      className="flex-1 border-green-500/30 text-green-400 hover:bg-green-500/10"
                    >
                      <Mail className="w-4 h-4 mr-1" />
                      Offre spéciale
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reply Dialog */}
      <Dialog open={replyDialogOpen} onOpenChange={setReplyDialogOpen}>
        <DialogContent className="bg-card border-border text-foreground max-w-lg">
          <DialogHeader>
            <DialogTitle>Répondre au message</DialogTitle>
          </DialogHeader>
          {selectedMessage && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Destinataire</Label>
                <p className="text-foreground font-medium">{selectedMessage.name} ({selectedMessage.email})</p>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <Label className="text-muted-foreground text-xs">Message original :</Label>
                <p className="text-sm text-foreground mt-1 whitespace-pre-wrap">{selectedMessage.message}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="replySubject">Objet</Label>
                <Input
                  id="replySubject"
                  value={replySubject}
                  onChange={(e) => setReplySubject(e.target.value)}
                  className="bg-input border-border"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="replyContent">Votre réponse</Label>
                <Textarea
                  id="replyContent"
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder="Écrivez votre réponse..."
                  className="bg-input border-border min-h-[150px]"
                />
              </div>
              <Button
                onClick={handleSendReply}
                disabled={sendingReply}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                <Send className="w-4 h-4 mr-2" />
                {sendingReply ? "Envoi en cours..." : "Envoyer la réponse"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Ticket Conversation Dialog */}
      <Dialog open={ticketDialogOpen} onOpenChange={setTicketDialogOpen}>
        <DialogContent className="bg-card border-border text-foreground max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              Conversation Ticket
            </DialogTitle>
          </DialogHeader>
          {selectedTicket && (
            <div className="flex flex-col gap-4 flex-1 min-h-0">
              {/* Info header */}
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg flex-shrink-0">
                <div>
                  <p className="font-medium text-foreground">
                    {getTicketEmail(selectedTicket) || "Email non fourni"}
                    {getTicketEmail(selectedTicket) && !selectedTicket.user_email && (
                      <span className="text-xs text-amber-400 ml-2">(détecté dans conversation)</span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(selectedTicket.created_at).toLocaleString("fr-FR")}
                  </p>
                </div>
                <Badge variant="destructive">Escaladé</Badge>
              </div>

              {/* Scrollable conversation */}
              <div className="flex-1 overflow-y-auto min-h-0 border border-border rounded-lg p-4 bg-muted/30">
                <div className="space-y-3">
                  {parseTicketMessages(selectedTicket.messages).map((msg, index) => (
                    <div 
                      key={index} 
                      className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div 
                        className={`max-w-[80%] p-3 rounded-lg ${
                          msg.role === "user" 
                            ? "bg-primary text-primary-foreground" 
                            : "bg-muted text-foreground"
                        }`}
                      >
                        <p className="text-xs font-semibold mb-1 opacity-70">
                          {msg.role === "user" ? "Client" : "Bot Alex"}
                        </p>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        {msg.timestamp && (
                          <p className="text-xs opacity-50 mt-1">
                            {new Date(msg.timestamp).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Reply section */}
              {(() => {
                const ticketEmail = getTicketEmail(selectedTicket);
                return ticketEmail ? (
                <div className="space-y-3 flex-shrink-0">
                  <Label htmlFor="ticketReply" className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Répondre par email à {ticketEmail}
                    {!selectedTicket.user_email && (
                      <span className="text-xs text-amber-400">(détecté dans la conversation)</span>
                    )}
                  </Label>
                  <Textarea
                    id="ticketReply"
                    value={ticketReplyContent}
                    onChange={(e) => setTicketReplyContent(e.target.value)}
                    placeholder="Écrivez votre réponse... (sera envoyée par email)"
                    className="bg-input border-border min-h-[100px]"
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={handleSendTicketReply}
                      disabled={sendingTicketReply || !ticketReplyContent.trim()}
                      className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {sendingTicketReply ? "Envoi en cours..." : "Envoyer par email"}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        handleResolveTicket(selectedTicket.id);
                        setTicketDialogOpen(false);
                      }}
                      className="border-green-500/50 text-green-400 hover:bg-green-900/50"
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Marquer résolu
                    </Button>
                  </div>
                </div>
                ) : (
                <div className="p-3 bg-amber-900/30 border border-amber-500/50 rounded-lg flex-shrink-0">
                  <p className="text-amber-300 text-sm">
                    ⚠️ Aucun email disponible. Le client n'a pas fourni son adresse email lors de la conversation.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      handleResolveTicket(selectedTicket.id);
                      setTicketDialogOpen(false);
                    }}
                    className="mt-2 border-green-500/50 text-green-400 hover:bg-green-900/50"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Marquer résolu sans réponse
                  </Button>
                </div>
                );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </div>
    </AdminErrorBoundary>
  );
};

export default AdminDashboard;
